import { addonIds } from "../../../../util/constants";

export const CREDIT_ADDON_IDS = [
  addonIds.CREDIT_CHECK,
  addonIds.SMS_CHASING_CREDITS,
  addonIds.PHONE_CALL_CHASING_CREDITS,
  addonIds.LETTER_CHASING_CREDITS,
];

export const ADDITIONAL_FEATURE_ADDON_IDS = [
  addonIds.TEMPLATE,
  addonIds.SCHEDULE,
  addonIds.EMAIL_INTEGRATION,
  addonIds.RECEIVABLES_FORECAST,
];

export const ICON_MAP = {
  [addonIds.TEMPLATE]: "statement",
  [addonIds.SCHEDULE]: "calendar",
  [addonIds.EMAIL_INTEGRATION]: "email-recipient",
  [addonIds.CREDIT_CHECK]: "search",
  [addonIds.RECEIVABLES_FORECAST]: "forecast",
  [addonIds.SMS_CHASING_CREDITS]: "sms",
  [addonIds.PHONE_CALL_CHASING_CREDITS]: "phone",
  [addonIds.LETTER_CHASING_CREDITS]: "email-letter",
};

export const ADDON_LABELS = {
  [addonIds.TEMPLATE]: "Templates",
  [addonIds.SCHEDULE]: "Schedules",
  [addonIds.PHONE_CALL_CHASING_CREDITS]: "Phone call chasing credits",
  [addonIds.SMS_CHASING_CREDITS]: "SMS chasing credits",
  [addonIds.CREDIT_CHECK]: "Credit check credits",
  [addonIds.LETTER_CHASING_CREDITS]: "Letter credits",
  [addonIds.EMAIL_INTEGRATION]: "Direct email integration",
};

const ADDON_LIMIT_LABELS = {
  sms: "SMS/month",
  phoneCall: "auto-calls/month",
  letter: "letters/month",
  emailIntegrationConnections: "email integrations",
};

const ADDON_FEATURE_KEYS = {
  sms: "freeSms",
  letter: "freeLetters",
  phoneCall: "freeCalls",
  users: "users",
  schedules: "schedules",
  emailIntegrationConnections: "emailIntegrationConnections",
  templates: "templates",
};

const FREE_CREDIT_ADDON_KEYS = new Set(["sms", "phoneCall", "letter"]);
const EXCLUDED_INCLUDED_BULLET_ADDON_KEYS = new Set([
  "receivablesForecasting",
  "tfpEmails",
  "users",
]);

export const toMinor = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
};

export const getUsageUnitPriceMinor = (addon, usage) => {
  const usagePriceMinor = toMinor(usage?.price);
  if (usagePriceMinor > 0) return usagePriceMinor;

  return toMinor(addon?.price || addon?.tiers?.[0]?.price);
};

export const toAmountMinorForUsage = (addon, usage) => {
  if (!addon || !usage) return 0;

  const unitPriceMinor = getUsageUnitPriceMinor(addon, usage);

  if (addon.type === "FLAT_FEE") {
    const isEnabled = toMinor(usage.overages) > 0 || Boolean(usage?.used);
    return isEnabled ? unitPriceMinor : 0;
  }

  const quantity = toMinor(usage.overages);
  return quantity * unitPriceMinor;
};

export const getRowActionLabel = (row) => {
  if (!row.isActive) return "Add to plan";
  if (row.addon.type === "FLAT_FEE") return "Remove";
  return "Adjust quantity";
};

const formatAllowanceValue = (value) => {
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 9999 ? "Unlimited" : String(value);
};

const resolveFeatureLimit = (features, addonKey) => {
  const key = ADDON_FEATURE_KEYS[addonKey] || addonKey;
  const rawValue = features?.[key];

  if (typeof rawValue === "boolean") {
    return null;
  }

  const numericValue = Number(rawValue);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue;
  }

  return null;
};

export const mapIncludedInPlanToPlanLimit = (addons = {}, features = {}) =>
  Object.entries(addons).reduce((acc, [addonKey, addonValue]) => {
    if (EXCLUDED_INCLUDED_BULLET_ADDON_KEYS.has(addonKey)) {
      return acc;
    }

    const addonType = addonValue?.type;
    const featureLimit = resolveFeatureLimit(features, addonKey);
    const rawPlanLimit = Number(addonValue?.planLimit);
    const shouldUseRawPlanLimit = !FREE_CREDIT_ADDON_KEYS.has(addonKey);
    const planLimit =
      Number.isFinite(featureLimit) && featureLimit > 0
        ? featureLimit
        : shouldUseRawPlanLimit &&
            Number.isFinite(rawPlanLimit) &&
            rawPlanLimit > 0
          ? rawPlanLimit
          : null;

    if (addonType === "FLAT_FEE") {
      if (!addonValue?.includedInPlan) return acc;
    } else if (addonType === "VOLUME" || addonType === "PER_UNIT") {
      if (!Number.isFinite(planLimit) || planLimit <= 0) return acc;
    } else if (!addonValue?.includedInPlan && (!planLimit || planLimit <= 0)) {
      return acc;
    }

    acc[addonKey] = {
      type: addonType,
      includedInPlan: Boolean(addonValue?.includedInPlan),
      planLimit: Number.isFinite(planLimit) ? planLimit : null,
    };

    return acc;
  }, {});

export const formatAddonLimitLabel = (addonKey) => {
  if (ADDON_LIMIT_LABELS[addonKey]) return ADDON_LIMIT_LABELS[addonKey];
  return addonKey
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/-/g, " ")
    .toLowerCase();
};

export const getPlanMaxRevenueText = (plan, currencyCode, formatCurrency) => {
  const maxRevenuePrice = plan?.features?.maximumRevenue?.prices?.find(
    (price) =>
      price.currency === currencyCode ||
      price.currency === String(currencyCode || "").toUpperCase(),
  );

  if (!maxRevenuePrice) return null;

  return `Up to ${formatCurrency(maxRevenuePrice.amount, currencyCode)} revenue`;
};

export const getIncludedDefaultLabel = (addonKey, addonData) => {
  const addonLabel = formatAddonLimitLabel(addonKey);

  if (addonData.type === "FLAT_FEE") {
    return {
      id: addonKey,
      label: `Included ${addonLabel}`,
    };
  }

  const displayValue = formatAllowanceValue(addonData.planLimit);
  if (!displayValue) return null;

  return {
    id: addonKey,
    label: `${displayValue} ${addonLabel}`,
  };
};

export const formatRecurringAmount = (
  amountMinor,
  formatCurrency,
  currencyCode,
  recurringSuffix,
) => `${formatCurrency(amountMinor / 100, currencyCode)}${recurringSuffix}`;

export const formatAddonFrom = (row, formatCurrency, currencyCode) => {
  const baseMinor = toMinor(row?.addon?.price || row?.addon?.tiers?.[0]?.price);
  return `From ${formatCurrency(baseMinor / 100, currencyCode)}`;
};
